import { Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { VenueTimezoneService } from '../../../../service/venue-timezone.service';
import { DisplayFormatService } from '../../../../shared/services/display-format.service';
import { CourtDurationOption } from '../court-grid.model';

@Component({
  selector: 'app-grid-selection-panel',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './grid-selection-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./grid-selection-panel.component.css'],
})
export class GridSelectionPanelComponent implements OnDestroy {
  private activeDragCleanup: (() => void) | null = null;
  /**
   * When false, the panel renders the mockup's "next step" empty state
   * (placeholder court name, hint text, disabled CTA) instead of a real
   * selection — lets the parent always mount this panel in day/week grid
   * mode rather than conditionally rendering it only once fully selected.
   */
  @Input() hasSelection = true;
  @Input() courtName = '';
  @Input() categoryName = '';
  @Input() selectedDate: Date = new Date();
  @Input() startTimeUtc = '';
  @Input() endTimeUtc = '';
  @Input() durationMinutes = 0;
  @Input() price = 0;
  @Input() availableDurations: CourtDurationOption[] = [];
  @Input() selectedDurationMinutes: number | null = null;

  @Output() durationChanged = new EventEmitter<number>();
  @Output() reserveClicked = new EventEmitter<void>();

  @ViewChild('sliderTrack', { static: false }) sliderTrack!: ElementRef<HTMLElement>;

  constructor(
    private venueTimezone: VenueTimezoneService,
    private displayFormat: DisplayFormatService,
    private ngZone: NgZone,
  ) {}

  ngOnDestroy(): void {
    this.activeDragCleanup?.();
  }

  get selectedSliderIndex(): number {
    const idx = this.availableDurations.findIndex(d => d.duration === this.selectedDurationMinutes);
    return idx >= 0 ? idx : 0;
  }

  get thumbPosition(): number {
    const count = this.availableDurations.length;
    return count <= 1 ? 0 : (this.selectedSliderIndex / (count - 1)) * 100;
  }

  dotPosition(index: number): number {
    const count = this.availableDurations.length;
    return count <= 1 ? 0 : (index / (count - 1)) * 100;
  }

  formatTime(isoInstant: string): string {
    try {
      return this.venueTimezone.formatTime(isoInstant);
    } catch {
      return '';
    }
  }

  formatDate(date: Date): string {
    return this.displayFormat.formatShortDateWithWeekday(date);
  }

  onTickClick(index: number): void {
    this.selectAtIndex(index);
  }

  /** Click on track → jump to nearest index */
  onTrackClick(event: MouseEvent | TouchEvent): void {
    // Don't handle if it originated from the thumb
    if ((event.target as HTMLElement).classList.contains('slider-thumb')) return;
    const index = this.indexFromEvent(event);
    if (index !== null) this.selectAtIndex(index);
  }

  /** Drag the thumb */
  onThumbDragStart(event: MouseEvent | TouchEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const onMove = (e: MouseEvent | TouchEvent) => {
      e.preventDefault();
      const index = this.indexFromEvent(e);
      if (index !== null && index !== this.selectedSliderIndex) {
        this.ngZone.run(() => this.selectAtIndex(index));
      }
    };
    const cleanup = () => {
      this.activeDragCleanup = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', cleanup);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', cleanup);
    };
    this.activeDragCleanup = cleanup;
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', cleanup);
      document.addEventListener('touchmove', onMove, { passive: false });
      document.addEventListener('touchend', cleanup);
    });
  }

  onReserveClick(): void {
    this.reserveClicked.emit();
  }

  /** Maps a mouse/touch event's X position to the nearest slider index */
  private indexFromEvent(event: MouseEvent | TouchEvent): number | null {
    if (!this.sliderTrack) return null;
    const rect = this.sliderTrack.nativeElement.getBoundingClientRect();
    const clientX = 'touches' in event
      ? (event.touches[0]?.clientX ?? event.changedTouches[0]?.clientX)
      : event.clientX;
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const count = this.availableDurations.length;
    return Math.round(ratio * (count - 1));
  }

  private selectAtIndex(index: number): void {
    const option = this.availableDurations[index];
    if (option?.available) {
      this.durationChanged.emit(option.duration);
      return;
    }
    const nearest = this.findNearestAvailable(index);
    if (nearest !== null) {
      this.durationChanged.emit(this.availableDurations[nearest].duration);
    }
  }

  private findNearestAvailable(fromIndex: number): number | null {
    for (let offset = 1; offset < this.availableDurations.length; offset++) {
      const below = fromIndex - offset;
      const above = fromIndex + offset;
      if (below >= 0 && this.availableDurations[below].available) return below;
      if (above < this.availableDurations.length && this.availableDurations[above].available) return above;
    }
    return null;
  }
}
